/**
 * Reads and writes URL search parameters for deep-linking test scenarios.
 *
 * Supported params:
 *   tag      - VAST tag URL
 *   xml      - Raw VAST XML (base64-encoded)
 *   preset   - Preset name
 *   env      - Environment profile name
 *   autostart - Auto-load on page load (true/false)
 *   autoplay - Enable autoplay at player init (true/false)
 *   muted    - Start muted (true/false)
 *   validate - Run validation after ad completes (true/false)
 *   export   - Auto-export session JSON to console (true/false)
 */

let currentConfig = {};

export function readUrlParams() {
  const params = new URLSearchParams(window.location.search);
  const mutedParam = params.get('muted');
  const autoplayParam = params.get('autoplay');
  currentConfig = {
    tag: params.get('tag') || null,
    xml: params.get('xml') ? atob(params.get('xml')) : null,
    preset: params.get('preset') || null,
    env: params.get('env') || null,
    autostart: params.get('autostart') === 'true',
    muted: mutedParam === null ? null : mutedParam !== 'false',
    autoplay: autoplayParam === null ? null : autoplayParam !== 'false',
    validate: params.get('validate') === 'true',
    export: params.get('export') === 'true',
  };
  return { ...currentConfig };
}

export function updateUrlParam(key, value) {
  currentConfig[key] = value;
  // Clear preset if tag changes manually, clear tag if preset is set
  if (key === 'tag') currentConfig.preset = null;
  if (key === 'preset') currentConfig.tag = null;
  _writeToUrl();
}

export function updateUrlParams(partial) {
  Object.assign(currentConfig, partial);
  _writeToUrl();
}

export function getShareableUrl() {
  const params = _buildParams();
  // Shared links always autostart
  params.set('autostart', 'true');
  // Shared autostart links should be autoplay-safe by default
  if (!params.has('muted')) {
    params.set('muted', 'true');
  }
  const qs = params.toString();
  return qs ? `${window.location.origin}${window.location.pathname}?${qs}` : window.location.origin;
}

export function getCurrentConfig() {
  return { ...currentConfig };
}

function _writeToUrl() {
  const params = _buildParams();
  const qs = params.toString();
  const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  window.history.replaceState(null, '', newUrl);
}

function _buildParams() {
  const params = new URLSearchParams();
  if (currentConfig.tag) params.set('tag', currentConfig.tag);
  if (currentConfig.xml) params.set('xml', btoa(currentConfig.xml));
  if (currentConfig.preset) params.set('preset', currentConfig.preset);
  if (currentConfig.env && currentConfig.env !== 'desktop') params.set('env', currentConfig.env);
  if (currentConfig.autostart) params.set('autostart', 'true');
  if (currentConfig.muted === true) params.set('muted', 'true');
  if (currentConfig.muted === false) params.set('muted', 'false');
  if (currentConfig.autoplay === true) params.set('autoplay', 'true');
  if (currentConfig.autoplay === false) params.set('autoplay', 'false');
  if (currentConfig.validate) params.set('validate', 'true');
  if (currentConfig.export) params.set('export', 'true');
  return params;
}
